import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { translations, getBrowserLanguage, bundledLanguages } from '../translations';
import {
  DEFAULT_PLATFORM_NAME,
  pickLocalized,
  resolveBranding
} from '../utils/platformDefaults';
import { applyThemeVariables } from '../utils/platformTheme';

const LanguageContext = createContext();

const DEFAULT_LANGUAGES = bundledLanguages;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const applyDocumentBranding = (name, logoUrl) => {
  if (typeof document === 'undefined') return;
  document.title = name || DEFAULT_PLATFORM_NAME;

  let iconLink = document.querySelector("link[rel='icon']");
  if (!iconLink) {
    iconLink = document.createElement('link');
    iconLink.setAttribute('rel', 'icon');
    document.head.appendChild(iconLink);
  }
  iconLink.setAttribute('href', logoUrl || '/favicon.ico');

  const description = document.querySelector('meta[name="description"]');
  if (description && name) {
    description.setAttribute('content', name);
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || getBrowserLanguage();
  });

  const [availableLanguages, setAvailableLanguages] = useState(DEFAULT_LANGUAGES);
  const [branding, setBranding] = useState(resolveBranding(null));

  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'global');
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      if (data.languages?.length > 0) {
        const fromSettings = data.languages
          .map((lang) => ({
            shortcut: String(lang?.shortcut || '').trim().toLowerCase(),
            name: String(lang?.name || '').trim()
          }))
          .filter((lang) => lang.shortcut && lang.name);
        setAvailableLanguages(fromSettings.length > 0 ? fromSettings : DEFAULT_LANGUAGES);
      } else {
        setAvailableLanguages(DEFAULT_LANGUAGES);
      }
      setBranding(resolveBranding(data));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const isAvailable = availableLanguages.some((l) => l.shortcut === currentLanguage);
    if (!isAvailable && availableLanguages.length > 0) {
      setCurrentLanguage(availableLanguages[0].shortcut);
    }
  }, [availableLanguages, currentLanguage]);

  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  const platformName = pickLocalized(branding.platformName, currentLanguage) || DEFAULT_PLATFORM_NAME;
  const joinNoticeText = pickLocalized(branding.joinNotice, currentLanguage);
  const logoUrl = branding.logoUrl || '';

  useEffect(() => {
    applyDocumentBranding(platformName, logoUrl);
    applyThemeVariables(branding);
  }, [platformName, logoUrl, branding]);

  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations.en?.[key] || key;
  };

  const setLanguage = (lang) => {
    if (lang === 'auto') {
      setCurrentLanguage(getBrowserLanguage());
    } else if (typeof lang === 'string' && lang.trim()) {
      setCurrentLanguage(lang.trim().toLowerCase());
    }
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        t,
        availableLanguages,
        setAvailableLanguages,
        branding,
        platformName,
        logoUrl,
        joinNoticeText,
        imprintUrl: branding.imprintUrl || '',
        privacyUrl: branding.privacyUrl || '',
        footerCreditName: branding.footerCreditName || '',
        footerCreditUrl: branding.footerCreditUrl || ''
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
